import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Alert,
  Box,
  CircularProgress,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { useParams } from "react-router-dom";

import GenericStaticSummary from "components/Records/RecordDisplay/GenericStaticSummary";
import PeopleInfoSection from "components/PeopleSummary/PeopleInfoSection";

import AttendanceSection from "./ConversationLogging/AttendanceSection";
import ConversationLogFormSection from "./ConversationLogging/ConversationLogFormSection";

import { getLinkedPeopleFromInteraction } from "api/Interactions/InteractionAPI";
import {
  conversationsAPI,
  conversationLogAPI,
} from "api/Interactions/ConversationsAPI";
import { peopleAPI } from "api/Community/PeopleAPI";
import { moveItemsBetweenNodes } from "api/Campaigns/CampaignAPI";

interface SimplePerson {
  id: number;
  primary: string;
}

interface AttendedPersonRecord {
  logId: number;
  person: any;
  logData: any;
  simple: SimplePerson;
}

const ConversationLogging: React.FC = () => {
  const { token } = useParams<{
    token: string;
  }>();

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    conversation,
    setConversation,
  ] = useState<any | null>(
    null
  );

  const [
    peopleList,
    setPeopleList,
  ] = useState<
    SimplePerson[]
  >([]);

  const [
    expectedPeopleList,
    setExpectedPeopleList,
  ] = useState<
    SimplePerson[]
  >([]);

  /*
   * This list is displayed by the shared attendance component.
   *
   * It is now populated from actual conversation logs, so it
   * represents historical attendees rather than simply people
   * added during the current browser session.
   */
  const [
    attendedPeopleList,
    setAttendedPeopleList,
  ] = useState<
    SimplePerson[]
  >([]);

  const [
    attendedPeople,
    setAttendedPeople,
  ] = useState<
    AttendedPersonRecord[]
  >([]);

  const [
    selectedPersonToEdit,
    setSelectedPersonToEdit,
  ] =
    useState<any | null>(
      null
    );

  const [
    selectedLogToEdit,
    setSelectedLogToEdit,
  ] =
    useState<
      number | null
    >(null);

  const [
    logNotes,
    setLogNotes,
  ] = useState("");

  const [
    attendanceLoadingPersonId,
    setAttendanceLoadingPersonId,
  ] = useState<
    number | null
  >(null);

  const [
    addingPeople,
    setAddingPeople,
  ] = useState(false);

  const [
    selectingPerson,
    setSelectingPerson,
  ] = useState(false);

  const externalAccessToPeopleDatabase =
    conversation
      ?.external_access_to_people_database ===
    true;

  const mapPerson = useCallback(
    (
      person: any
    ): SimplePerson => ({
      id: Number(person.id),

      primary: `${
        person.first_name ??
        ""
      } ${
        person.last_name ??
        ""
      }`.trim(),
    }),
    []
  );

  const attendedPersonIds =
    useMemo(
      () =>
        Array.from(
          new Set(
            attendedPeople.map(
              (record) =>
                Number(
                  record.simple.id
                )
            )
          )
        ),
      [attendedPeople]
    );

  const selectedPersonId =
    selectedPersonToEdit?.id !==
      undefined &&
    selectedPersonToEdit?.id !==
      null
      ? Number(
          selectedPersonToEdit.id
        )
      : null;

  const selectedPersonIsAttended =
    selectedPersonId !== null &&
    attendedPersonIds.includes(
      selectedPersonId
    );

  const findAttendanceRecord =
    useCallback(
      (personId: number) =>
        attendedPeople.find(
          (record) =>
            Number(
              record.simple.id
            ) ===
            Number(personId)
        ),
      [attendedPeople]
    );

  /*
   * Load all conversation logs.
   *
   * The logs are now the source of truth for the
   * "Attended People" list.
   */
  const fetchAttendedPeople =
    useCallback(async () => {
      if (!conversation) {
        return;
      }

      const logs =
        await conversationLogAPI.getAllByParent(
          Number(
            conversation.id
          ),
          token
        );

      const mapped =
        await Promise.all(
          logs.map(
            async (
              log: any
            ) => {
              const personId =
                log?.people?.id ??
                log?.people ??
                log?.people_id ??
                null;

              if (
                personId ===
                  null ||
                personId ===
                  undefined
              ) {
                return null;
              }

              const person =
                token
                  ? await peopleAPI.getByIdWithInteractionToken(
                      Number(
                        personId
                      ),
                      token
                    )
                  : await peopleAPI.getById(
                      Number(
                        personId
                      )
                    );

              return {
                logId:
                  Number(
                    log.id
                  ),

                person,

                logData:
                  log,

                simple:
                  mapPerson(
                    person
                  ),
              };
            }
          )
        );

      const validRecords =
        mapped.filter(
          Boolean
        ) as AttendedPersonRecord[];

      setAttendedPeople(
        validRecords
      );

      /*
       * Build the visible historical attendee list directly
       * from conversation logs.
       *
       * De-duplicate by person id in case old duplicate logs
       * already exist in the database.
       */
      const uniqueAttendees =
        Array.from(
          new Map(
            validRecords.map(
              (record) => [
                Number(
                  record.simple.id
                ),
                record.simple,
              ]
            )
          ).values()
        );

      setAttendedPeopleList(
        uniqueAttendees
      );
    }, [
      conversation,
      mapPerson,
      token,
    ]);

  /*
   * Load the conversation and its current expected people.
   *
   * Expected People represents people who are currently
   * sitting in the pathway node linked to this conversation.
   */
  useEffect(() => {
    if (!token) {
      return;
    }

    let active = true;

    const fetchConversation =
      async () => {
        try {
          const data =
            await conversationsAPI.getByToken(
              token
            );

          const linkedPeople =
            await getLinkedPeopleFromInteraction(
              "conversation",
              Number(
                data.id
              )
            );

          if (!active) {
            return;
          }

          setExpectedPeopleList(
            linkedPeople.map(
              mapPerson
            )
          );

          setConversation(
            data
          );
        } catch (error) {
          console.error(
            "Could not load conversation:",
            error
          );

          if (active) {
            setLoading(
              false
            );
          }
        }
      };

    fetchConversation();

    return () => {
      active = false;
    };
  }, [
    mapPerson,
    token,
  ]);

  /*
   * Load people available through the database picker ONLY
   * when the conversation allows external database access.
   */
  useEffect(() => {
    if (!conversation) {
      return;
    }

    if (
      !externalAccessToPeopleDatabase
    ) {
      setPeopleList(
        []
      );

      setLoading(
        false
      );

      return;
    }

    let active = true;

    const fetchPeople =
      async () => {
        try {
          const list =
            token
              ? await peopleAPI.getAllWithInteractionToken(
                  token
                )
              : await peopleAPI.getAll();

          if (!active) {
            return;
          }

          setPeopleList(
            list.map(
              mapPerson
            )
          );
        } catch (error) {
          console.error(
            "Could not load people:",
            error
          );
        } finally {
          if (active) {
            setLoading(
              false
            );
          }
        }
      };

    fetchPeople();

    return () => {
      active = false;
    };
  }, [
    conversation,
    externalAccessToPeopleDatabase,
    mapPerson,
    token,
  ]);

  /*
   * Load historical attendees once the conversation exists.
   */
  useEffect(() => {
    if (!conversation) {
      return;
    }

    fetchAttendedPeople();
  }, [
    conversation,
    fetchAttendedPeople,
  ]);

  /*
   * Mark people as attended.
   *
   * We do NOT manually create the conversation log here.
   * moveItemsBetweenNodes() creates the correct interaction
   * log through the backend pathway logging system.
   */
  const createAttendanceLogs =
    async (
      selectedPeople:
        SimplePerson[]
    ) => {
      if (
        !conversation ||
        selectedPeople.length ===
          0
      ) {
        return;
      }

      const conversationId =
        Number(
          conversation.id
        );

      const uniquePeople =
        Array.from(
          new Map(
            selectedPeople.map(
              (person) => [
                Number(
                  person.id
                ),
                {
                  ...person,
                  id: Number(
                    person.id
                  ),
                },
              ]
            )
          ).values()
        );

      const existingLogs =
        await conversationLogAPI.getAllByParent(
          conversationId,
          token
        );

      const existingPersonIds =
        new Set<number>(
          existingLogs
            .map(
              (
                log: any
              ) =>
                log
                  ?.people
                  ?.id ??
                log?.people ??
                log?.people_id ??
                null
            )
            .filter(
              (
                id: any
              ) =>
                id !==
                  null &&
                id !==
                  undefined &&
                id !==
                  ""
            )
            .map(
              (
                id: any
              ) =>
                Number(
                  id
                )
            )
            .filter(
              (
                id: number
              ) =>
                !Number.isNaN(
                  id
                )
            )
        );

      const peopleToCreate =
        uniquePeople.filter(
          (person) =>
            !existingPersonIds.has(
              Number(
                person.id
              )
            )
        );

      if (
        peopleToCreate.length >
        0
      ) {
        const result =
          await moveItemsBetweenNodes(
            {
              item_field_name:
                "people",

              selected_items:
                peopleToCreate.map(
                  (
                    person
                  ) =>
                    Number(
                      person.id
                    )
                ),

              handle_name:
                "output-2",

              direction:
                "forward",

              interaction_id:
                conversationId,

              interaction_type:
                "conversation",
            }
          );

        /*
         * Once the backend has successfully moved the people
         * out of this interaction's pathway node, remove them
         * from the local Expected People list as well.
         *
         * Their newly created logs will put them into
         * Attended People instead.
         */
        if (
          Number(
            result?.moved ??
              0
          ) > 0
        ) {
          const movedIds =
            new Set(
              peopleToCreate.map(
                (
                  person
                ) =>
                  Number(
                    person.id
                  )
              )
            );

          setExpectedPeopleList(
            (previous) =>
              previous.filter(
                (person) =>
                  !movedIds.has(
                    Number(
                      person.id
                    )
                  )
              )
          );
        }
      }
    };

  const addPeople =
    async (
      selectedPeople:
        SimplePerson[]
    ) => {
      if (
        selectedPeople.length ===
        0
      ) {
        return;
      }

      setAddingPeople(
        true
      );

      try {
        /*
         * Newly added people are treated as attended immediately.
         * The backend pathway move creates their interaction logs.
         */
        await createAttendanceLogs(
          selectedPeople
        );

        /*
         * Refresh from the logs so Attended People reflects
         * what was actually saved by the backend.
         */
        await fetchAttendedPeople();

        await selectPerson(
          selectedPeople[0]
        );
      } catch (error) {
        console.error(
          "Could not add people:",
          error
        );
      } finally {
        setAddingPeople(
          false
        );
      }
    };

  const selectPerson =
    async (
      personItem:
        SimplePerson
    ) => {
      setSelectingPerson(
        true
      );

      setSelectedPersonToEdit(
        null
      );

      setSelectedLogToEdit(
        null
      );

      setLogNotes("");

      try {
        const freshPerson =
          token
            ? await peopleAPI.getByIdWithInteractionToken(
                Number(
                  personItem.id
                ),
                token
              )
            : await peopleAPI.getById(
                Number(
                  personItem.id
                )
              );

        const attendanceRecord =
          findAttendanceRecord(
            Number(
              personItem.id
            )
          );

        if (
          attendanceRecord
        ) {
          const freshLog =
            await conversationLogAPI.getById(
              attendanceRecord.logId
            );

          setSelectedLogToEdit(
            Number(
              freshLog.id
            )
          );

          setLogNotes(
            freshLog?.notes ??
              ""
          );
        } else if (
          conversation
        ) {
          /*
           * Fall back to the backend in case React attendance
           * state has not finished updating yet.
           */
          const logs =
            await conversationLogAPI.getAllByParent(
              Number(
                conversation.id
              ),
              token
            );

          const matchingLog =
            logs.find(
              (
                log: any
              ) =>
                Number(
                  log
                    ?.people
                    ?.id ??
                    log?.people ??
                    log?.people_id
                ) ===
                Number(
                  personItem.id
                )
            );

          if (
            matchingLog
          ) {
            setSelectedLogToEdit(
              Number(
                matchingLog.id
              )
            );

            setLogNotes(
              matchingLog?.notes ??
                ""
            );
          }
        }

        setSelectedPersonToEdit(
          freshPerson
        );
      } catch (error) {
        console.error(
          "Could not select person:",
          error
        );
      } finally {
        setSelectingPerson(
          false
        );
      }
    };

  const removeAttendance =
    async (
      personId: number
    ) => {
      let attendanceRecord =
        findAttendanceRecord(
          personId
        );

      if (
        !attendanceRecord &&
        conversation
      ) {
        const logs =
          await conversationLogAPI.getAllByParent(
            Number(
              conversation.id
            ),
            token
          );

        const matchingLog =
          logs.find(
            (
              log: any
            ) =>
              Number(
                log
                  ?.people
                  ?.id ??
                  log?.people ??
                  log?.people_id
              ) ===
              Number(
                personId
              )
          );

        if (
          matchingLog
        ) {
          attendanceRecord =
            {
              logId:
                Number(
                  matchingLog.id
                ),

              person: null,

              logData:
                matchingLog,

              simple: {
                id: Number(
                  personId
                ),
                primary:
                  "",
              },
            };
        }
      }

      if (
        !attendanceRecord
      ) {
        return;
      }

      await conversationLogAPI.delete(
        attendanceRecord.logId
      );

      if (
        selectedPersonId ===
        personId
      ) {
        setSelectedLogToEdit(
          null
        );

        setLogNotes("");
      }
    };

  const setAttendance =
    async (
      person:
        SimplePerson,
      attended:
        boolean
    ) => {
      const personId =
        Number(
          person.id
        );

      setAttendanceLoadingPersonId(
        personId
      );

      try {
        if (attended) {
          await createAttendanceLogs(
            [person]
          );
        } else {
          await removeAttendance(
            personId
          );
        }

        /*
         * This refreshes both:
         * - attendedPeople records used for editing
         * - attendedPeopleList used for display
         */
        await fetchAttendedPeople();

        if (
          selectedPersonId ===
          personId
        ) {
          if (
            attended &&
            conversation
          ) {
            const logs =
              await conversationLogAPI.getAllByParent(
                Number(
                  conversation.id
                ),
                token
              );

            const newLog =
              logs.find(
                (
                  log: any
                ) =>
                  Number(
                    log
                      ?.people
                      ?.id ??
                      log?.people ??
                      log?.people_id
                  ) ===
                  personId
              );

            if (
              newLog
            ) {
              setSelectedLogToEdit(
                Number(
                  newLog.id
                )
              );

              setLogNotes(
                newLog?.notes ??
                  ""
              );
            }
          } else {
            setSelectedLogToEdit(
              null
            );

            setLogNotes(
              ""
            );
          }
        }
      } catch (error) {
        console.error(
          "Could not update attendance:",
          error
        );
      } finally {
        setAttendanceLoadingPersonId(
          null
        );
      }
    };

  const submitForm =
    async () => {
      if (
        !selectedLogToEdit ||
        !conversation
      ) {
        return;
      }

      const updated =
        await conversationLogAPI.update(
          selectedLogToEdit,
          {
            conversation:
              Number(
                conversation.id
              ),

            notes:
              logNotes,
          }
        );

      setAttendedPeople(
        (previous) =>
          previous.map(
            (record) =>
              record.logId ===
              selectedLogToEdit
                ? {
                    ...record,

                    logData:
                      {
                        ...record.logData,

                        notes:
                          updated?.notes ??
                          logNotes,
                      },
                  }
                : record
          )
      );
    };

  const updateMappedPersonInList =
    (
      people:
        SimplePerson[],

      updatedPerson:
        SimplePerson
    ) =>
      people.map(
        (person) =>
          Number(
            person.id
          ) ===
          Number(
            updatedPerson.id
          )
            ? updatedPerson
            : person
      );

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 1600,
        mx: "auto",

        px: {
          xs: 1,
          sm: 2,
          md: 3,
        },

        pb: {
          xs: 3,
          md: 4,
        },
      }}
    >
      <GenericStaticSummary
        record={conversation}
        titleField="name"
        descriptionField="notes"
        loading={loading}
      />

      <Grid2
        container
        spacing={{
          xs: 1.5,
          sm: 2,
          md: 3,
        }}
        alignItems="flex-start"
        sx={{
          mt: {
            xs: 0.5,
            sm: 1,
          },
        }}
      >
        <Grid2
          size={{
            xs: 12,
            lg: 5,
          }}
        >
          <AttendanceSection
            addedPeople={
              attendedPeopleList
            }
            expectedPeople={
              expectedPeopleList
            }
            peopleList={
              peopleList
            }
            attendedPersonIds={
              attendedPersonIds
            }
            selectedPersonId={
              selectedPersonId
            }
            loading={
              loading
            }
            attendanceLoadingPersonId={
              attendanceLoadingPersonId
            }
            addingDatabasePeople={
              addingPeople
            }
            externalAccessToPeopleDatabase={
              externalAccessToPeopleDatabase
            }
            addPeople={
              addPeople
            }
            setAttendance={
              setAttendance
            }
            selectPerson={
              selectPerson
            }
          />
        </Grid2>

        <Grid2
          size={{
            xs: 12,
            lg: 7,
          }}
          sx={{
            minWidth: 0,
          }}
        >
          {selectingPerson ? (
            <Box
              sx={{
                minHeight: {
                  xs: 140,
                  sm: 180,
                },

                display:
                  "flex",

                alignItems:
                  "center",

                justifyContent:
                  "center",
              }}
            >
              <CircularProgress />
            </Box>
          ) : selectedPersonToEdit ? (
            <Box
              sx={{
                minWidth: 0,

                display:
                  "flex",

                flexDirection:
                  "column",

                gap: {
                  xs: 1.5,
                  sm: 2,
                },
              }}
            >
              <PeopleInfoSection
                person={
                  selectedPersonToEdit
                }
                interactionToken={
                  token
                }
                onPersonUpdated={(
                  updated
                ) => {
                  setSelectedPersonToEdit(
                    updated
                  );

                  const mapped =
                    mapPerson(
                      updated
                    );

                  setPeopleList(
                    (
                      previous
                    ) =>
                      updateMappedPersonInList(
                        previous,
                        mapped
                      )
                  );

                  setExpectedPeopleList(
                    (
                      previous
                    ) =>
                      updateMappedPersonInList(
                        previous,
                        mapped
                      )
                  );

                  setAttendedPeopleList(
                    (
                      previous
                    ) =>
                      updateMappedPersonInList(
                        previous,
                        mapped
                      )
                  );

                  setAttendedPeople(
                    (
                      previous
                    ) =>
                      previous.map(
                        (
                          record
                        ) =>
                          Number(
                            record
                              .simple
                              .id
                          ) ===
                          Number(
                            mapped.id
                          )
                            ? {
                                ...record,

                                person:
                                  updated,

                                simple:
                                  mapped,
                              }
                            : record
                      )
                  );
                }}
              />

              {selectedPersonIsAttended &&
              selectedLogToEdit ? (
                <ConversationLogFormSection
                  notes={
                    logNotes
                  }
                  setNotes={
                    setLogNotes
                  }
                  onSubmit={
                    submitForm
                  }
                />
              ) : (
                <Alert
                  severity="info"
                  sx={{
                    width:
                      "100%",
                  }}
                >
                  Mark this
                  person as
                  attended
                  before adding
                  conversation
                  notes.
                </Alert>
              )}
            </Box>
          ) : null}
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default ConversationLogging;